import { type CSSProperties, useId } from 'react';

// V2 Mid-century Modern California Home — scene SVG ported from
// .gstack/design-reports/discloser-co-20260512/variants/v2-midcentury-modern.html
// Used in the Hero scroll-reveal: fades in behind the document spread as the
// user scrolls through the pinned hero runway, then fades out as they continue.
//
// Gradient/pattern IDs are prefixed with useId() so the scene can render in
// multiple places on the same page without DOM collisions.

interface HeroHomeSceneProps {
  /** 0 = fully hidden, 1 = fully visible */
  opacity?: number;
  /** Subtle entry scale (e.g. 0.94 → 1) */
  scale?: number;
  className?: string;
  style?: CSSProperties;
}

export default function HeroHomeScene({
  opacity = 1,
  scale = 1,
  className,
  style,
}: HeroHomeSceneProps) {
  const u = useId().replace(/:/g, '');
  const id = (suffix: string) => `${u}-${suffix}`;
  return (
    <div
      className={className}
      style={{
        opacity,
        transform: `scale(${scale})`,
        transformOrigin: 'center center',
        ...style,
      }}
      aria-hidden
    >
      <svg
        viewBox="0 0 680 700"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Mid-century modern California home"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <defs>
          <linearGradient id={id('skyGrad')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7BA8D4" />
            <stop offset="55%" stopColor="#9EBFE5" />
            <stop offset="100%" stopColor="#C8DDED" />
          </linearGradient>
          <linearGradient id={id('groundGrad')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ECDDD0" />
            <stop offset="100%" stopColor="#E0CDBC" />
          </linearGradient>
          <linearGradient id={id('glassMain')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#B8D5EB" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#7BA8D4" stopOpacity="0.25" />
          </linearGradient>
          <linearGradient id={id('glassReflect')} x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={id('shadowUnder')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0D0A09" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0D0A09" stopOpacity="0" />
          </linearGradient>
          <radialGradient id={id('sunGlow')} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FAF7F5" stopOpacity="0.6" />
            <stop offset="60%" stopColor="#9EBFE5" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* SKY: intentionally not painted. The hero section's gradient
            background shows through, integrating the scene into the page
            instead of giving it a self-contained sky box. */}

        {/* SUN — layered warm discs (no embedded mark) */}
        <circle cx="562" cy="108" r="72" fill={`url(#${id('sunGlow')})`} />
        <circle cx="562" cy="108" r="48" fill="#EAD8C6" opacity="0.7" />
        <circle cx="562" cy="108" r="36" fill="#DCCAB6" opacity="0.85" />
        <circle cx="562" cy="108" r="22" fill="#D65C1F" opacity="0.78" />

        {/* PALM */}
        <path
          d="M630 520 Q634 440 628 360 Q625 300 631 240"
          stroke="#2A1F0E"
          strokeWidth="9"
          fill="none"
          strokeLinecap="round"
        />
        <path d="M631 245 C 665 230 695 252 680 285 C 665 272 650 260 631 245 Z" fill="#1C3018" opacity="0.9" />
        <path d="M631 245 C 658 210 690 198 688 230 C 672 225 654 228 631 245 Z" fill="#243D1E" opacity="0.85" />
        <path d="M631 245 C 640 200 655 185 660 205 C 648 212 638 226 631 245 Z" fill="#1F3619" opacity="0.9" />
        <path d="M631 245 C 612 195 594 182 598 210 C 611 216 622 229 631 245 Z" fill="#243D1E" opacity="0.85" />
        <path d="M631 245 C 595 228 567 248 576 278 C 592 264 610 253 631 245 Z" fill="#1C3018" opacity="0.9" />
        <path d="M631 245 C 604 250 585 270 591 298 C 604 281 616 264 631 245 Z" fill="#16280F" opacity="0.8" />
        <line x1="631" y1="245" x2="679" y2="283" stroke="#122010" strokeWidth="1.5" opacity="0.5" />
        <line x1="631" y1="245" x2="687" y2="229" stroke="#122010" strokeWidth="1.5" opacity="0.5" />
        <line x1="631" y1="245" x2="659" y2="205" stroke="#122010" strokeWidth="1.5" opacity="0.5" />
        <line x1="631" y1="245" x2="598" y2="210" stroke="#122010" strokeWidth="1.5" opacity="0.5" />
        <line x1="631" y1="245" x2="577" y2="277" stroke="#122010" strokeWidth="1.5" opacity="0.5" />
        <line x1="631" y1="245" x2="591" y2="297" stroke="#122010" strokeWidth="1.5" opacity="0.5" />

        {/* GROUND */}
        <rect x="0" y="510" width="680" height="190" fill={`url(#${id('groundGrad')})`} />
        <path d="M60 520 L620 520 L640 540 L640 700 L60 700 Z" fill="#D9CABC" opacity="0.6" />
        <line x1="60" y1="520" x2="640" y2="520" stroke="#C0B09E" strokeWidth="1.5" />
        <line x1="95" y1="520" x2="95" y2="700" stroke="#C4B39E" strokeWidth="1.5" opacity="0.5" />
        <line x1="130" y1="520" x2="130" y2="700" stroke="#C4B39E" strokeWidth="1.5" opacity="0.5" />
        <line x1="165" y1="520" x2="165" y2="700" stroke="#C4B39E" strokeWidth="1.5" opacity="0.5" />
        <rect x="195" y="522" width="430" height="18" fill="#BEAC97" opacity="0.35" />

        {/* MAIN ROOF */}
        <rect x="160" y="200" width="470" height="28" fill="#171312" />
        <rect x="160" y="228" width="470" height="5" fill="#0D0A09" />
        <rect x="52" y="218" width="148" height="22" fill="#171312" />
        <rect x="52" y="240" width="148" height="4" fill="#0D0A09" />
        <rect x="160" y="228" width="470" height="20" fill={`url(#${id('shadowUnder')})`} opacity="0.5" />
        <rect x="160" y="196" width="470" height="4" fill="#2A2320" />

        {/* CLERESTORY BAND */}
        <rect x="162" y="170" width="466" height="30" fill="#C8B9A8" />
        <rect x="170" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="170" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <line x1="196" y1="173" x2="196" y2="197" stroke="#9EBFE5" strokeWidth="0.8" opacity="0.6" />
        <rect x="226" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="226" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <line x1="252" y1="173" x2="252" y2="197" stroke="#9EBFE5" strokeWidth="0.8" opacity="0.6" />
        <rect x="282" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="282" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <rect x="338" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="338" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <rect x="394" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="394" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <line x1="420" y1="173" x2="420" y2="197" stroke="#9EBFE5" strokeWidth="0.8" opacity="0.6" />
        <rect x="450" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="450" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <line x1="476" y1="173" x2="476" y2="197" stroke="#9EBFE5" strokeWidth="0.8" opacity="0.6" />
        <rect x="506" y="173" width="52" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="506" y="173" width="52" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <rect x="562" y="173" width="56" height="24" fill={`url(#${id('glassMain')})`} rx="1" />
        <rect x="562" y="173" width="56" height="24" fill={`url(#${id('glassReflect')})`} rx="1" />
        <line x1="162" y1="171" x2="628" y2="171" stroke="#171312" strokeWidth="1.5" />
        <line x1="162" y1="197" x2="628" y2="197" stroke="#171312" strokeWidth="1.5" />

        {/* MAIN HOUSE BODY */}
        <rect x="162" y="248" width="110" height="272" fill="#D4C0AA" />
        <rect x="272" y="248" width="356" height="272" fill="#B8D4E9" opacity="0.3" />
        <rect x="272" y="248" width="83" height="272" fill={`url(#${id('glassMain')})`} />
        <rect x="272" y="248" width="83" height="272" fill={`url(#${id('glassReflect')})`} />
        <rect x="361" y="248" width="83" height="272" fill={`url(#${id('glassMain')})`} />
        <rect x="361" y="248" width="83" height="272" fill={`url(#${id('glassReflect')})`} />
        <rect x="450" y="248" width="83" height="272" fill={`url(#${id('glassMain')})`} />
        <rect x="450" y="248" width="83" height="272" fill={`url(#${id('glassReflect')})`} />
        <rect x="539" y="248" width="88" height="272" fill={`url(#${id('glassMain')})`} />
        <rect x="539" y="248" width="88" height="272" fill={`url(#${id('glassReflect')})`} />
        <rect x="355" y="248" width="6" height="272" fill="#171312" />
        <rect x="444" y="248" width="6" height="272" fill="#171312" />
        <rect x="533" y="248" width="6" height="272" fill="#171312" />
        <rect x="622" y="244" width="8" height="276" fill="#171312" />
        <rect x="274" y="310" width="344" height="140" fill="#C4A882" opacity="0.12" />
        <rect x="272" y="330" width="358" height="4" fill="#171312" opacity="0.18" />

        {/* ENTRY DOOR */}
        <rect x="196" y="340" width="54" height="180" fill="#D65C1F" opacity="0.08" />
        <rect x="196" y="340" width="54" height="180" fill="none" stroke="#171312" strokeWidth="2.5" />
        <rect x="196" y="340" width="54" height="6" fill="#0D0A09" opacity="0.2" />
        <line x1="218" y1="427" x2="232" y2="427" stroke="#171312" strokeWidth="3" strokeLinecap="round" />
        <rect x="250" y="360" width="22" height="140" fill={`url(#${id('glassMain')})`} />
        <rect x="250" y="360" width="22" height="140" fill={`url(#${id('glassReflect')})`} />
        <rect x="250" y="360" width="22" height="140" fill="none" stroke="#171312" strokeWidth="1.5" />
        <rect x="162" y="244" width="110" height="8" fill="#171312" />
        <line x1="175" y1="256" x2="175" y2="520" stroke="#BCA98E" strokeWidth="1" opacity="0.5" />
        <line x1="188" y1="256" x2="188" y2="520" stroke="#BCA98E" strokeWidth="1" opacity="0.4" />

        {/* RUST FASCIA */}
        <rect x="160" y="228" width="470" height="20" fill="#D65C1F" />
        <rect x="160" y="244" width="470" height="4" fill="#0D0A09" opacity="0.3" />

        {/* CARPORT */}
        <rect x="60" y="490" width="148" height="30" fill="#C8B9A8" opacity="0.7" />
        <rect x="68" y="244" width="7" height="276" fill="#171312" />
        <rect x="112" y="244" width="7" height="276" fill="#171312" />
        <rect x="156" y="244" width="7" height="276" fill="#171312" />
        <rect x="54" y="240" width="148" height="8" fill="#8B6A42" opacity="0.7" />
        <line x1="54" y1="252" x2="202" y2="252" stroke="#7A5C35" strokeWidth="2.5" opacity="0.5" />
        <line x1="54" y1="262" x2="202" y2="262" stroke="#7A5C35" strokeWidth="2" opacity="0.35" />
        <line x1="54" y1="272" x2="202" y2="272" stroke="#7A5C35" strokeWidth="2" opacity="0.25" />
        <rect x="54" y="240" width="148" height="10" fill="#D65C1F" opacity="0.85" />

        {/* FOUNDATION */}
        <rect x="162" y="516" width="470" height="8" fill="#171312" opacity="0.5" />
        <rect x="60" y="516" width="102" height="8" fill="#171312" opacity="0.35" />

        {/* LANDSCAPE — agave / grasses / boulders */}
        <path d="M148 520 C 152 490 158 475 162 465 C 166 475 172 490 176 520 Z" fill="#1C3018" opacity="0.88" />
        <path d="M136 520 C 140 498 145 488 148 482 C 151 488 154 498 157 520 Z" fill="#243D1E" opacity="0.75" />
        <line x1="162" y1="465" x2="147" y2="520" stroke="#0F1E0C" strokeWidth="1" opacity="0.6" />
        <line x1="162" y1="465" x2="162" y2="520" stroke="#0F1E0C" strokeWidth="1.5" opacity="0.5" />
        <line x1="162" y1="465" x2="177" y2="520" stroke="#0F1E0C" strokeWidth="1" opacity="0.6" />
        <ellipse cx="100" cy="522" rx="24" ry="8" fill="#B8A690" opacity="0.65" />
        <ellipse cx="76" cy="524" rx="14" ry="5" fill="#C4B09A" opacity="0.55" />
        <path d="M590 520 C 594 498 596 480 595 462 C 597 480 602 498 604 520 Z" fill="#2A3F1C" opacity="0.7" />
        <path d="M604 520 C 606 502 609 486 610 472 C 613 486 615 502 616 520 Z" fill="#1F3016" opacity="0.75" />
        <path d="M580 520 C 582 506 584 494 582 484 C 585 494 588 506 590 520 Z" fill="#243818" opacity="0.65" />
        <ellipse cx="555" cy="524" rx="30" ry="10" fill="#B8A690" opacity="0.5" />
        <ellipse cx="575" cy="520" rx="18" ry="7" fill="#C8B6A0" opacity="0.45" />

        {/* SHADOWS */}
        <ellipse cx="393" cy="522" rx="230" ry="6" fill="#0D0A09" opacity="0.18" />
        <rect x="162" y="248" width="470" height="32" fill="#0D0A09" opacity="0.08" />
        <line x1="0" y1="510" x2="680" y2="510" stroke="#BCA98E" strokeWidth="0.8" opacity="0.3" />
      </svg>
    </div>
  );
}
